import cookies from 'js-cookie';
import {classList, dataset, lazyCustomElements, listen} from 'nuffjs';
import {ucFirst} from '../utils/string';
import {Dialog} from '../dialog/dialog';

/**
 * Configuration values
 *
 * @property {string} actionAttr=action
 * @property {string} groupAttr=group
 * @property {string} trackerAttr=tracker
 * @property {string} delayAttr=delay
 * @property {string} anchorAttr=anchor
 * @property {string} hiddenClass=hidden
 * @property {string} expandedClass=expanded
 * @property {string} confirmModeClass=confirmMode
 * @property {string} trackersDetailsId=trackersDetails
 *
 * @memberof Trackers
 * @extends Dialog.conf
 */
const conf = Object.assign({}, Dialog.conf, {
	actionAttr: 'action',
	groupAttr: 'group',
	trackerAttr: 'tracker',
	delayAttr: 'delay',
	anchorAttr: 'anchor',
	hiddenClass: 'hidden',
	expandedClass: 'expanded',
	confirmModeClass: 'confirmMode',
	trackersDetailsId: 'trackersDetails',
});

/**
 * @classdesc Custom dialog used to handle the so called "cookie consent". Content is controlled in
 * Kirby Panel in the Privacy tab. Trackers are described and grouped and for each of them a code
 * snipped is provided. When the user express their consent (which can be selective for certain
 * trackers, excluding other ones), the snippet of code is applied to the page and the user choice
 * is stored in a cookie. If that cookie is present, the snippet are applied automatically without
 * opening the dialog again. The user can always reopen the dialog to change their preference
 * through a special anchor link, configurable in the panel. The dialog starts as non modal and no
 * tracker is applied until the user express or deny the consent. When the user starts to interact
 * with the option the dialog become modal.
 *
 * @class Trackers
 * @extends {Dialog}
 * @version 1.0.0
 * @path
 *
 */
class Trackers extends Dialog {
	static conf = conf;

	/**
	 * @property {boolean} modal=true
	 * @property {boolean} closeOnEsc=false disable the closing without expressing consent/denial
	 * @property {boolean} closeOnClick=false disable the closing without expressing consent/denial
	 * @property {string} cookieName='' name used for the cookie with user's preferences
	 * @property {boolean} cookieDuration=0 cookie duration
	 * @property {string} separator=- separator used in groups and trackers names
	 * @property {boolean} delay=0 delay to open the dialog after the page loads
	 * @property {string} anchor='' anchor configured to reopen the dialog after it has been already closed
	 * @property {boolean} trackers={} list of grouped trackers
	 * @property {boolean} trackersEmbedCode={} list of trackers codes to be eventually embedded in the page
	 *
	 * @static
	 * @memberof Trackers
	 * @extends Dialog.defaultOpts
	 */
	static defaultOpts = Object.assign({}, Dialog.defaultOpts, {
		modal: true,
		closeOnEsc: false,
		closeOnClick: false,
		cookieName: '',
		cookieDuration: 0,
		separator: '-',
		delay: 0,
		anchor: '',
		trackers: {},
		trackersEmbedCode: {},
	});

	trackers = new Map();
	confirmMode = false;
	expanded = false;
	details = null;

	/**
	 * Initialization. Show the dialog if the preferences are not saved yet, otherwise process the
	 * previously saved preferences.
	 *
	 * @memberof Trackers
	 */
	firstConnectedCallback() {
		super.firstConnectedCallback();
		this.opts = dataset(this).get(this.constructor.defaultOpts);
		this.initData(this.opts.trackers);

		listen(this, 'click', this.onAction.bind(this), {selector: dataset.selector(conf.actionAttr)});
		listen(this, 'change', this.onInputChange.bind(this), {selector: 'input[type="radio"]'});
		listen(document, 'click', this.onReopenClick.bind(this), {
			capture: true,
			selector: `a[href$="#${this.opts.anchor}"]`,
		});

		const cookieData = cookies.getJSON(this.opts.cookieName);
		if (cookieData) {
			this.applyTrackers(cookieData);
			this.confirmMode = true;
		} else {
			setTimeout(() => {
				this.show();
			}, this.opts.delay * 1000);
		}
	}

	/**
	 * Retrieve the component (typically a collapsable) which is containing the detailed information
	 * about the trackers
	 *
	 * @returns {Promise<HTMLElement>} the custom element containing the details
	 * @async
	 * @memberof Trackers
	 */
	async getDetailsElement() {
		if (!this.details) {
			this.details = await lazyCustomElements.querySelector(
				this,
				dataset.selector('id', conf.trackersDetailsId)
			);
		}
		return this.details;
	}

	/**
	 * Handle the reopening of the dialog through a link click
	 *
	 * @param {Event} event the click event on a link to a specific anchor
	 * @memberof Trackers
	 */
	onReopenClick(event) {
		event.preventDefault();
		this.show();
	}

	/**
	 * Generic handler that routes the specific actions to dedicated methods
	 *
	 * @param {Event} event the event handled
	 * @param {Element} target the element dispatching the event
	 * @memberof Trackers
	 */
	onAction(event, target) {
		const actionName = dataset(target).get(conf.actionAttr);
		const method = 'on' + ucFirst(actionName);
		this[method](event, target);
	}

	/**
	 * Handle the toggling of the details
	 *
	 * @param {Event} event the event that is generating the toggling
	 * @memberof Trackers
	 */
	async onToggleDetails(event) {
		this.toggle();
		this.expanded = !this.expanded;
		classList(this).toggle(conf.expandedClass, this.expanded);
		const details = await this.getDetailsElement();
		details.toggle();
	}

	/**
	 * Handles changes in the user preferences updating the internal data structure accordingly
	 *
	 * @param {Event} event a change event coming from nested input elements
	 * @param {HTMLElement} target the input dispatching the event
	 * @memberof Trackers
	 */
	onInputChange(event, target) {
		const data = dataset(target).get();
		const groupName = data[conf.groupAttr];
		const trackerName = conf.trackerAttr in data ? data[conf.trackerAttr] : null;
		if (trackerName !== null) {
			this.updateTracker(groupName, trackerName, target.value);
		} else {
			this.updateGroup(groupName, target.value);
		}
	}

	/**
	 * Handle the save action
	 *
	 * @param {Event} event the click event on the dedicated button
	 * @memberof Trackers
	 */
	onSave(event) {
		this.finalize();
	}

	/**
	 * Handle the 'ok' action, to confirm the expressed preferences
	 *
	 * @param {Event} event the click event on the dedicated button
	 * @memberof Trackers
	 */
	onOk(event) {
		this.finalize();
	}

	/**
	 * Handle the 'reject all' action
	 *
	 * @param {Event} event the click event on the dedicated button
	 * @memberof Trackers
	 */
	onRejectAll(event) {
		this.updateAll(0);
	}

	/**
	 * Handle the 'accept all' action
	 *
	 * @param {Event} event the click event on the dedicated button
	 * @memberof Trackers
	 */
	onAcceptAll(event) {
		this.updateAll(1);
	}

	/**
	 * Prepare the internal data structures processing the input data
	 *
	 * @param {object} trackers the key > value representation of grouped trackers
	 * @memberof Trackers
	 * @protected
	 * @ignore
	 */
	initData(trackers) {
		const sep = this.opts.separator;
		this.trackers.clear();
		for (const groupName in trackers) {
			if (trackers.hasOwnProperty(groupName)) {
				const group = {
					trackers: new Map(),
					selected: 0,
					input: [
						this.querySelector(`#${groupName}${sep}0`),
						this.querySelector(`#${groupName}${sep}1`),
					],
				};
				this.trackers.set(groupName, group);
				for (const trackerName in trackers[groupName]) {
					if (trackers[groupName].hasOwnProperty(trackerName)) {
						if (!trackers[groupName][trackerName].required) {
							const tracker = Object.assign(
								{
									input: [
										this.querySelector(`#${groupName}${sep}${trackerName}${sep}0`),
										this.querySelector(`#${groupName}${sep}${trackerName}${sep}1`),
									],
								},
								trackers[groupName][trackerName]
							);
							tracker.input[0].checked = tracker.value === 0;
							tracker.input[1].checked = tracker.value === 1;
							group.trackers.set(trackerName, tracker);
							group.selected += tracker.value;
						}
					}
				}
				if (group.trackers.size > 0) {
					group.input[0].checked = group.selected === 0;
					group.input[1].checked = group.selected === group.trackers.size;
				}
			}
		}
	}

	/**
	 * Assign a certain value to all the trackers across all the groups.
	 * Used when "accept all" or "refuse all" action are performed.
	 *
	 * @param {number} value 1 = accept, 0 = refuse
	 * @memberof Trackers
	 */
	updateAll(value) {
		for (const groupName of this.trackers.keys()) {
			this.updateGroup(groupName, value);
		}
		this.finalize();
	}

	/**
	 * Assign a certain value to all the trackers belonging to a certain group
	 *
	 * @param {string} groupName the name of the group to be updated
	 * @param {number} value 1 = accept, 0 = refuse
	 * @memberof Trackers
	 */
	updateGroup(groupName, value) {
		value = parseInt(value, 10);
		const group = this.trackers.get(groupName);
		group.selected = value ? group.trackers.size : 0;
		for (const tracker of group.trackers.values()) {
			tracker.value = value;
			tracker.input[0].checked = value === 0;
			tracker.input[1].checked = value === 1;
		}
	}

	/**
	 * Assign a certain value to a very specific tracker belonging to a specific group
	 *
	 * @param {string} groupName the name of the group to be updated
	 * @param {string} trackerName the name of the tracker inside the group
	 * @param {number} value 1 = accept, 0 = refuse
	 * @memberof Trackers
	 */
	updateTracker(groupName, trackerName, value) {
		value = parseInt(value, 10);
		const group = this.trackers.get(groupName);
		const tracker = group.trackers.get(trackerName);
		if (value !== tracker.value) {
			tracker.value = value;
			group.selected += value === 1 ? 1 : -1;
			group.input[0].checked = group.selected === 0;
			group.input[1].checked = group.selected === group.trackers.size;
		}
	}

	/**
	 * Perform the necessary actions after the user express their preferences.
	 *
	 * - save the new preferences in the cookie
	 * - hides the dialog
	 * - if the dialog was reopened and the new preferences are different from the previous
	 *   preferences, it forces a page reload in order to have only the proper tracker embedded in
	 *   the page (removing a script node from a page after the script is executed does not stop the
	 *   execution of the eventual script loaded in memory, the only way is to refresh the page and
	 *   not embed the script in the first place), otherwise applies the user preferences on the
	 *   current page
	 *
	 * @memberof Trackers
	 */
	finalize() {
		const previousData = cookies.getJSON(this.opts.cookieName);
		const data = this.extractData();
		this.save(data);
		this.hide();
		if (this.confirmMode) {
			if (previousData && JSON.stringify(previousData) !== JSON.stringify(data)) {
				window.location.reload();
			}
		} else {
			this.applyTrackers(data);
		}
	}

	/**
	 * Extract the information from the internal data structure in a format suitable to be saved in
	 * the cookie
	 *
	 * @returns {object} the data ready to be stored in the cookie
	 * @memberof Trackers
	 */
	extractData() {
		const data = {};
		for (const [groupName, group] of this.trackers) {
			data[groupName] = {};
			for (const [trackerName, tracker] of group.trackers) {
				data[groupName][trackerName] = {
					value: tracker.value,
				};
			}
		}
		return data;
	}

	/**
	 * Store the preferences in the cookie
	 *
	 * @param {object} data
	 * @memberof Trackers
	 */
	save(data) {
		cookies.set(this.opts.cookieName, data, {expires: this.opts.cookieDuration});
	}

	/**
	 * Apply the user preferences, meaning it embeds the allowed snippets of HTML code in the page, if any
	 *
	 * @param {object} data data expressing the user consent / denial for every tracker
	 * @memberof Trackers
	 */
	applyTrackers(data) {
		for (const groupName in data) {
			if (data.hasOwnProperty(groupName)) {
				const group = data[groupName];
				for (const trackerName in group) {
					if (group.hasOwnProperty(trackerName)) {
						const tracker = group[trackerName];
						// if the user consent and there is actual code to embed, then we proceed
						if (
							tracker.value === 1 &&
							groupName in this.opts.trackersEmbedCode &&
							trackerName in this.opts.trackersEmbedCode[groupName] &&
							this.opts.trackersEmbedCode[groupName][trackerName] !== null
						) {
							const div = document.createElement('div');
							div.innerHTML = this.opts.trackersEmbedCode[groupName][trackerName];
							for (const node of div.childNodes) {
								// if you append directly the script it does not execute
								// it needs to be recreated
								if (node instanceof HTMLScriptElement) {
									const script = document.createElement('script');
									for (const attribute of node.attributes) {
										script.setAttribute(attribute.name, attribute.value);
									}
									script.textContent = node.textContent;
									document.body.appendChild(script);
								} else {
									document.body.appendChild(node);
								}
							}
						}
					}
				}
			}
		}
	}

	/**
	 * Show the trackers dialog
	 *
	 * @memberof Trackers
	 */
	show() {
		if (this.confirmMode) {
			classList(this).add(conf.confirmModeClass);
		}
		classList(this).remove(conf.hiddenClass);
	}

	/**
	 * Hide the trackers dialog
	 *
	 * @memberof Trackers
	 */
	async hide() {
		if (this.expanded) {
			await this.close();
			const details = await this.getDetailsElement();
			details.collapse();
		}
		classList(this).add(conf.hiddenClass);
		classList(this).remove(conf.expandedClass);
	}
}

export {Trackers};
